import { Cake, CalendarDays, PartyPopper, Megaphone, Users, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { employees } from "../data/employees";
import { holidays } from "../data/holidays";
import { getUpcomingBirthdays, getUpcomingAnniversaries, getNextHoliday } from "../utils/dashboardUtils";
import { useAuth } from "../auth/AuthContext";
import { PageHeader, Card, Empty } from "../components/ui";

const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });

function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const birthdays = getUpcomingBirthdays(employees);
  const anniversaries = getUpcomingAnniversaries(employees);
  const nextHoliday = getNextHoliday(holidays);
  const canSeeAnnouncements = user.role === "admin" || user.modules.includes("announcements");
  const firstName = (user.fullName || user.username).split(" ")[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back, ${firstName}`}
        subtitle="Company updates, upcoming dates and celebrations at Lightspeed Photonics."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <Card>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <CalendarDays size={15} className="text-[#C00000]" /> Next Holiday
          </div>
          {nextHoliday ? (
            <div className="mt-4 flex items-end gap-4">
              <p className="text-5xl font-semibold text-[#C00000] leading-none tabular-nums">
                {new Date(nextHoliday.date).getDate()}
              </p>
              <div className="pb-0.5">
                <p className="font-semibold text-[#111111]">{nextHoliday.name}</p>
                <p className="text-sm text-gray-500">
                  {new Date(nextHoliday.date).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                </p>
              </div>
            </div>
          ) : (
            <Empty>No upcoming holidays</Empty>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <Users size={15} className="text-[#C00000]" /> Upcoming Event
          </div>
          <div className="mt-4 flex items-end gap-4">
            <p className="text-5xl font-semibold text-[#C00000] leading-none tabular-nums">12</p>
            <div className="pb-0.5">
              <p className="font-semibold text-[#111111]">Investor Visit</p>
              <p className="text-sm text-gray-500">All demos must be showcased</p>
            </div>
          </div>
        </Card>

        <Card
          className={canSeeAnnouncements ? "cursor-pointer hover:border-[#C00000]/40 transition-colors" : ""}
        >
          <div
            onClick={() => canSeeAnnouncements && navigate("/dashboard/announcements")}
            role={canSeeAnnouncements ? "button" : undefined}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <Megaphone size={15} className="text-[#C00000]" /> Latest Announcement
              </div>
              {canSeeAnnouncements && <ArrowUpRight size={16} className="text-gray-400" />}
            </div>
            <p className="mt-4 font-semibold text-[#111111]">WiFi Network Update</p>
            <p className="text-sm text-gray-500 mt-1">All WiFi routers are installed and configured successfully.</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <PeopleList
          title="Upcoming Birthdays"
          icon={<Cake size={15} className="text-[#C00000]" />}
          people={birthdays}
          dateKey="nextBirthday"
          avatar="bg-[#C00000]"
        />
        <PeopleList
          title="Work Anniversaries"
          icon={<PartyPopper size={15} className="text-[#C00000]" />}
          people={anniversaries}
          dateKey="nextAnniversary"
          avatar="bg-[#111111]"
        />
      </div>
    </div>
  );
}

function PeopleList({ title, icon, people, dateKey, avatar }) {
  return (
    <Card
      padded={false}
      title={
        <span className="inline-flex items-center gap-2">{icon}{title}</span>
      }
    >
      {people.length === 0 ? (
        <Empty>Nothing coming up.</Empty>
      ) : (
        <ul className="divide-y divide-gray-100">
          {people.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-5 py-3.5">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full ${avatar} text-white text-sm font-semibold flex items-center justify-center shrink-0`}>
                  {p.name.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#111111] truncate">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.id}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-700 tabular-nums">{fmt(p[dateKey])}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default Home;
